import React, { useState } from 'react';

interface MetricCardProps {
  title: string;
  value: number | string;
  description?: string;
  descriptionColor?: string;
  frontValueColor?: string;
  backValueColor?: string;
  frontCardColor?: string;
  backCardColor?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  isNegative?: boolean;
  canFlip?: boolean;
  backTitle?: string;
  backValue?: number | string;
  backDescription?: string;
  backDescriptionColor?: string;
  backIcon?: React.ReactNode;
  onNoFlipClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  description,
  descriptionColor,
  frontValueColor,
  backValueColor,
  frontCardColor='#fff',
  backCardColor='#f4ffed',
  icon,
  trend,
  trendValue,
  isNegative = false,
  canFlip = false,
  backTitle,
  backValue,
  backDescription,
  backDescriptionColor,
  backIcon,
  onNoFlipClick
}) => {
  const [isFlipped, setIsFlipped] = useState(false);

  
  const handleFlip = () => {
    if (canFlip) {
      setIsFlipped(!isFlipped);
    }
    else if (onNoFlipClick) {
      onNoFlipClick();
    }
  };

  const cardStyle = {    
    borderRadius: 12,
    
    height: '100%',
    width: '100%',
    cursor: canFlip ? 'pointer' : 'default',
    transition: 'transform 0.6s',
    transformStyle: 'preserve-3d' as const,
    position: 'relative' as const,
    transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
    margin: 0
  };

  const cardFaceStyle = {
    backfaceVisibility: 'hidden' as const,
    position: canFlip ? 'absolute' as const : 'relative' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    borderRadius: 12
  };

  const frontStyle = {
    ...cardFaceStyle,
    transform: 'rotateY(0deg)',
    boxShadow: '6px 4px 12px rgba(0, 0, 0, 0.3)',
    background: frontCardColor
  };

  const backStyle = {
    ...cardFaceStyle,
    transform: 'rotateY(180deg)',
    background: backCardColor,
    boxShadow: '6px 4px 12px rgba(0, 0, 0, 0.3)',
  };

  const renderCardContent = (isBackSide: boolean) => (
    <>
      <div 
      className='metric-card-content'
      style={{
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        margin: '1.0rem'}}>
        <div>
          <h3 style={{
            margin: 0,
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#666',
            marginBottom: '0.5rem',
            minHeight: '35px'
          }}>
            {isBackSide ? (backTitle || title) : title}
          </h3>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: 700,
            color: isBackSide ? backValueColor : frontValueColor
          }}>
            {isBackSide ? (backValue !== undefined ? backValue : value) : value}
          </div>
        </div>
        {(isBackSide ? (backIcon || icon) : icon) && (
          <div style={{
            backgroundColor: `${(isBackSide ? backValueColor : frontValueColor)}20`,
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isBackSide ? (backIcon || icon) : icon}
          </div>
        )}
      </div>

      {(isBackSide ? (backDescription || description) : description) && (
        <p style={{
          margin: '0 1rem 0.5rem',
          fontSize: '0.875rem',
          color: isBackSide && backDescriptionColor ? backDescriptionColor : descriptionColor,
          minHeight: '35px'
        }}>
          {isBackSide ? (backDescription || description) : description}
        </p>
      )}

      {trend && (
        <div style={{          
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.875rem',
          color: trend === 'up'
            ? (isNegative ? '#e53935' : '#43a047')
            : trend === 'down'
              ? (isNegative ? '#43a047' : '#e53935')
              : '#666'
        }}>
          {trend === 'up' && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 15l-6-6-6 6" />
            </svg>
          )}
          {trend === 'down' && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          )}
          <span style={{ marginLeft: '0.25rem' }}>{trendValue}</span>
        </div>
      )}
      
      {canFlip && (
        <div style={{
          bottom: '0.5rem',
          marginBottom: '0.5rem',
          fontSize: '0.75rem',
          color: 'blue',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          {isBackSide ? 'Click to see overview' : 'Click to see trend information'}
        </div>
      )}
    </>
  );

  return (
    <div 
      onClick={handleFlip}
      style={{
        perspective: '1000px',
        height: '100%',
        minHeight: '180px',
        width: '100%'
      }}
    >
      <div style={cardStyle}>
        <div className='metric-card-front-wrapper' style={frontStyle}>
          {renderCardContent(false)}
        </div>

        {canFlip && (
          <div className='metric-card-back-wrapper' style={backStyle}>
            {renderCardContent(true)}
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricCard;
